import { atom } from 'jotai';

import { dateToEpochString } from '@flipster/utils/datetime';
import type { OrderMarginType } from '@flipster/utils/types/base';
import type { TimestampString } from '@flipster/utils/typescript';

import { refPositionTimes } from '../../server-time';
import { updateV2SocketData } from '../v2-helper';
import { createTableAccumulatorBunja } from './table-accumulator';

export interface ProfitBoost {
  cost: string;
  costDiscounted: string | null; // null if no discount
  durationInMin: number;
}

export interface Option {
  direction: 'Put' | 'Call';
  strike: string;
  qty: string;
}

export interface Position {
  positionId: string;
  avgEntryPrice: string;
  cumFee: string;
  hasLiquidationPrice: boolean; // deprecated 예정
  initLeverage: number;
  effectiveLeverage?: string;
  isolatedMarginInitial: string;
  isolatedMarginAdded?: string;
  isolatedMarginRealizedFee: string;
  isolatedMarginExecutedFee: string;
  /**
   * margin amount in single position
   */
  initPositionMargin: string;
  /**
   * margin amount in single position, but includes margin of one-way pending order
   */
  initMarginReserved: string;
  liquidationPrice?: string; // 마이너스 값이 오는경우 '-'로 표시
  position: string; // short인 경우 마이너스 값
  slot: number; // Position이 할당된 slot 번호
  symbol: string;
  unrealizedPnl: string;
  unrealizedPnlRef: string;
  unrealizedPnlMark?: string;
  unrealizedPnlLast?: string;
  unrealizedPnlBbo?: string;
  netPnl: string;
  netPnlMark?: string;
  netPnlLast?: string;
  netPnlBbo?: string;
  netRealizedPnl: string;
  grossRealizedPnl: string;
  cumFundingFee: string;
  cumTradingFee: string;
  midPrice: string;
  bidPrice: string;
  askPrice: string;
  airdropType: null | 'Launch' | 'Manual';
  lockedUntil?: TimestampString;
  bboPriceTimestamp: TimestampString;
  openTime: TimestampString;
  buyableBoosts: ProfitBoost[];
  boostExpiryTimestamp: TimestampString | null;
  boostDailyLimitCount: number;
  boostDailyRemainingCount: number;
  boostCumPnl: string | null;
  marginType: OrderMarginType;
  options: Option[];
}

export type Positions = Record<string, Position> | null;

export const positionsAtom = atom<Positions>(null);

export const subscribePositionsBunja = createTableAccumulatorBunja('private/positions', (store) => (wsData) => {
  store.set(positionsAtom, (prev) => {
    const positions = updateV2SocketData(prev || {}, wsData);
    Object.entries(wsData).forEach(([action, values]) => {
      if (action === 's' || action === 'i' || action === 'u') {
        Object.entries(values as Record<string, Partial<Position>>).forEach(([positionKey, value]) => {
          const p = positions?.[positionKey];
          if (p && value.bboPriceTimestamp) {
            refPositionTimes[p.symbol] = {
              refServerTimestamp: value.bboPriceTimestamp,
              refClientTimestamp: dateToEpochString(new Date()),
            };
          }
        });
      }
    });
    return positions;
  });
});
subscribePositionsBunja.debugLabel = 'subscribePositionsBunja';
